import { useQuery } from '@tanstack/react-query';

import React, { useEffect, useMemo, useState } from 'react';

import {
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	MenuItem,
	Paper,
	Select,
	Stack,
	Typography,
	alpha,
} from '@mui/material';

import { useApiClient, useToastService } from '@@common';

import { ClientClient, ClientDependencyModel, ClientDependencyRequest, ComponentModel, ContextClient, ContextScopeModel, DeepOperationModel, OperationsClient, SetClientDependenciesRequest } from '../../../api/api';
import { sortByName } from '../../../common/sortByName';

const TARGET_TYPE_OPERATION = 'Operation';
const TARGET_TYPE_SCOPE = 'Scope';

interface AddClientDependencyDialogProps {
	open: boolean;
	clientId: string;
	components: ComponentModel[];
	existingDependencies: ClientDependencyModel[];
	initialComponentId?: string;
	scopeOnly?: boolean;
	onClose: () => void;
	onDependencyAdded: () => void;
}

export default function AddClientDependencyDialog({
	open,
	clientId,
	components,
	existingDependencies,
	initialComponentId,
	scopeOnly = false,
	onClose,
	onDependencyAdded,
}: AddClientDependencyDialogProps) {
	const client = useApiClient(ClientClient) as ClientClient;
	const contextClient = useApiClient(ContextClient) as ContextClient;
	const operationsClient = useApiClient(OperationsClient) as OperationsClient;
	const toastService = useToastService();
	const [componentId, setComponentId] = useState('');
	const [selectedOperationIds, setSelectedOperationIds] = useState<string[]>([]);
	const [selectedScopeIds, setSelectedScopeIds] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	const selectedComponent = useMemo(() => components.find((component) => component.id === componentId), [components, componentId]);
	const existingTargetIdSet = useMemo(
		() =>
			new Set(
				existingDependencies
					.filter((dependency) => !selectedComponent || dependency.dependsOnComponentId === selectedComponent.id)
					.map((dependency) => dependency.targetId),
			),
		[existingDependencies, selectedComponent],
	);
	const assignedScopeIdsForSelectedComponent = useMemo(
		() =>
			existingDependencies
				.filter((dependency) => dependency.targetType === TARGET_TYPE_SCOPE && dependency.dependsOnComponentId === selectedComponent?.id)
				.map((dependency) => dependency.targetId),
		[existingDependencies, selectedComponent?.id],
	);
	const availableComponents = useMemo(
		() => components.filter((component) => !!component.contextId).sort(sortByName),
		[components],
	);

	const { data: contextData, isFetching: isFetchingContext } = useQuery({
		queryKey: ['client-dependency-context', selectedComponent?.contextId],
		queryFn: () => contextClient.getContextById(selectedComponent!.contextId!),
		enabled: open && !!selectedComponent?.contextId,
	});

	const { data: operations = [], isFetching: isFetchingOperations } = useQuery({
		queryKey: ['client-dependency-operations', selectedComponent?.contextId],
		queryFn: () => operationsClient.getOperationsForContext(selectedComponent!.contextId!),
		enabled: open && !!selectedComponent?.contextId,
	});

	const contextScopes = useMemo(() => [...(contextData?.scopes ?? [])].sort(sortByName), [contextData?.scopes]);
	const contextScopeIdSet = useMemo(() => new Set(contextScopes.map((scope) => scope.id)), [contextScopes]);
	const staleAssignedScopes = useMemo<ScopeListItem[]>(
		() =>
			assignedScopeIdsForSelectedComponent
				.filter((scopeId) => !contextScopeIdSet.has(scopeId))
				.map((scopeId) => ({
					id: scopeId,
					name: scopeId,
					description: 'Previously assigned, but this scope no longer exists on the component.',
					isMissing: true,
				})),
		[assignedScopeIdsForSelectedComponent, contextScopeIdSet],
	);
	const existingScopeIds = useMemo(
		() => (scopeOnly ? assignedScopeIdsForSelectedComponent : []),
		[assignedScopeIdsForSelectedComponent, scopeOnly],
	);
	const availableScopes = useMemo(
		() => (scopeOnly ? [...contextScopes, ...staleAssignedScopes] : contextScopes.filter((scope) => !existingTargetIdSet.has(scope.id))),
		[contextScopes, existingTargetIdSet, scopeOnly, staleAssignedScopes],
	);

	const availableOperations = useMemo(
		() => operations.filter((operation) => !existingTargetIdSet.has(operation.id)).sort(sortByName),
		[existingTargetIdSet, operations],
	);

	const isLoadingOptions = isFetchingContext || isFetchingOperations;
	const hasSelection = selectedOperationIds.length > 0 || selectedScopeIds.length > 0;
	const selectedScopeIdSet = useMemo(() => new Set(selectedScopeIds), [selectedScopeIds]);
	const existingScopeIdSet = useMemo(() => new Set(existingScopeIds), [existingScopeIds]);
	const scopeSelectionHasChanges = useMemo(
		() => scopeOnly && !areSetsEqual(selectedScopeIdSet, existingScopeIdSet),
		[existingScopeIdSet, scopeOnly, selectedScopeIdSet],
	);
	const canSave = scopeOnly ? !!selectedComponent && scopeSelectionHasChanges : !!selectedComponent && hasSelection;

	useEffect(() => {
		if (open && initialComponentId) {
			setComponentId(initialComponentId);
			setSelectedOperationIds([]);
			setSelectedScopeIds(scopeOnly ? existingScopeIds : []);
		}
	}, [existingScopeIds, initialComponentId, open, scopeOnly]);

	const handleClose = () => {
		if (isSaving) return;
		resetSelection();
		onClose();
	};

	const resetSelection = () => {
		setComponentId(initialComponentId ?? '');
		setSelectedOperationIds([]);
		setSelectedScopeIds(scopeOnly ? existingScopeIds : []);
	};

	const handleComponentChange = (nextComponentId: string) => {
		setComponentId(nextComponentId);
		setSelectedOperationIds([]);
		setSelectedScopeIds([]);
	};

	const handleSave = async () => {
		if (!canSave || !selectedComponent) return;

		setIsSaving(true);
		try {
			const selectedScopeIdsForSave = new Set(selectedScopeIds);
			const scopeIdsToAdd = scopeOnly
				? selectedScopeIds.filter((scopeId) => !existingScopeIdSet.has(scopeId))
				: selectedScopeIds;
			const scopeIdsToRemove = scopeOnly
				? existingScopeIds.filter((scopeId) => !selectedScopeIdsForSave.has(scopeId))
				: [];
			const dependencies: ClientDependencyRequest[] = [
				...(scopeOnly
					? []
					: selectedOperationIds.map((operationId) => ({
							dependsOnComponentId: selectedComponent.id,
							targetId: operationId,
							targetType: TARGET_TYPE_OPERATION,
						}))),
				...scopeIdsToAdd.map((scopeId) => ({
					dependsOnComponentId: selectedComponent.id,
					targetId: scopeId,
					targetType: TARGET_TYPE_SCOPE,
				})),
			];

			const request: SetClientDependenciesRequest = {
				add: dependencies,
				remove: scopeIdsToRemove,
			};

			await client.setDependencies(clientId, request);
			toastService.success(
				scopeOnly
					? `Updated scopes for ${selectedComponent.name}.`
					: `Added ${dependencies.length} permission ${dependencies.length === 1 ? 'dependency' : 'dependencies'} for ${selectedComponent.name}.`,
			);
			resetSelection();
			onDependencyAdded();
		} catch (error) {
			console.error('Error adding client dependency:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to add dependency: ${errorMessage}`);
		} finally {
			setIsSaving(false);
		}
	};

	const toggleScope = (scopeId: string) => {
		setSelectedScopeIds((current) => (current.includes(scopeId) ? current.filter((id) => id !== scopeId) : [...current, scopeId]));
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>{scopeOnly ? 'Manage Client Scopes' : 'Add Client Permissions'}</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					<FormControl fullWidth>
						<InputLabel id="client-dependency-component-label">Component</InputLabel>
						<Select
							labelId="client-dependency-component-label"
							label="Component"
							value={componentId}
							disabled={scopeOnly && !!initialComponentId}
							onChange={(event) => handleComponentChange(event.target.value)}
						>
							{availableComponents.map((component) => (
								<MenuItem key={component.id} value={component.id}>
									{component.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{scopeOnly ? (
						<ScopeChecklist
							scopes={availableScopes}
							selectedScopeIds={selectedScopeIdSet}
							disabled={!selectedComponent || isLoadingOptions}
							onToggleScope={toggleScope}
						/>
					) : (
						<FormControl fullWidth disabled={!selectedComponent || isLoadingOptions}>
							<InputLabel id="client-dependency-scopes-label">Scopes</InputLabel>
							<Select
								labelId="client-dependency-scopes-label"
								label="Scopes"
								multiple
								value={selectedScopeIds}
								onChange={(event) => setSelectedScopeIds(normalizeSelectedIds(event.target.value))}
								renderValue={(selected) => renderScopeNames(selected as string[], availableScopes)}
							>
								{availableScopes.map((scope) => (
									<MenuItem key={scope.id} value={scope.id}>
										<Checkbox checked={selectedScopeIds.includes(scope.id)} />
										<ListItemText primary={scope.name} secondary={scope.description} />
									</MenuItem>
								))}
								{availableScopes.length === 0 && (
									<MenuItem disabled>
										<ListItemText primary="No additional scopes available" />
									</MenuItem>
								)}
							</Select>
						</FormControl>
					)}

					{!scopeOnly && (
						<FormControl fullWidth disabled={!selectedComponent || isLoadingOptions}>
							<InputLabel id="client-dependency-operations-label">Operations</InputLabel>
							<Select
								labelId="client-dependency-operations-label"
								label="Operations"
								multiple
								value={selectedOperationIds}
								onChange={(event) => setSelectedOperationIds(normalizeSelectedIds(event.target.value))}
								renderValue={(selected) => renderOperationNames(selected as string[], availableOperations)}
							>
								{availableOperations.map((operation) => (
									<MenuItem key={operation.id} value={operation.id}>
										<Checkbox checked={selectedOperationIds.includes(operation.id)} />
										<ListItemText primary={operation.name} secondary={operation.description} />
									</MenuItem>
								))}
								{availableOperations.length === 0 && (
									<MenuItem disabled>
										<ListItemText primary="No additional operations available" />
									</MenuItem>
								)}
							</Select>
						</FormControl>
					)}

					<Typography variant="caption" color="text.secondary">
						{scopeOnly
							? 'Scopes grant direct access to the component app registration.'
							: 'Scopes grant direct access to the component app registration. Operations grant the scopes assigned to those operations.'}
					</Typography>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button onClick={handleSave} disabled={isSaving || !canSave} variant="contained">
					{isSaving ? 'Saving...' : scopeOnly ? 'Save' : 'Add'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

function renderScopeNames(selectedIds: string[], scopes: ContextScopeModel[]) {
	const scopeNameById = new Map(scopes.map((scope) => [scope.id, scope.name]));
	return selectedIds.map((id) => scopeNameById.get(id) ?? id).join(', ');
}

function renderOperationNames(selectedIds: string[], operations: DeepOperationModel[]) {
	const operationNameById = new Map(operations.map((operation) => [operation.id, operation.name]));
	return selectedIds.map((id) => operationNameById.get(id) ?? id).join(', ');
}

function normalizeSelectedIds(value: string | string[]) {
	return typeof value === 'string' ? value.split(',') : value;
}

function areSetsEqual(left: Set<string>, right: Set<string>) {
	if (left.size !== right.size) return false;
	return Array.from(left).every((value) => right.has(value));
}

interface ScopeChecklistProps {
	scopes: ScopeListItem[];
	selectedScopeIds: Set<string>;
	disabled: boolean;
	onToggleScope: (scopeId: string) => void;
}

interface ScopeListItem extends ContextScopeModel {
	isMissing?: boolean;
}

function ScopeChecklist({ scopes, selectedScopeIds, disabled, onToggleScope }: ScopeChecklistProps) {
	return (
		<Stack spacing={1}>
			<Typography variant="subtitle2">Scopes</Typography>
			<Paper variant="outlined" sx={{ maxHeight: 280, overflow: 'auto' }}>
				{scopes.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
						No additional scopes available
					</Typography>
				) : (
					<List dense disablePadding>
						{scopes.map((scope, index) => (
							<ListItem
								key={scope.id}
								disablePadding
								divider={index < scopes.length - 1}
								sx={{
									...(scope.isMissing && {
										bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
										'&:hover': {
											bgcolor: (theme) => alpha(theme.palette.warning.main, 0.18),
										},
									}),
								}}
							>
								<ListItemButton dense disabled={disabled} onClick={() => onToggleScope(scope.id)}>
									<Checkbox edge="start" checked={selectedScopeIds.has(scope.id)} tabIndex={-1} disableRipple />
									<ListItemText
										primary={
											<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
												<Typography variant="body2" noWrap color={scope.isMissing ? 'warning.main' : undefined}>
													{scope.name}
												</Typography>
												{scope.isMissing && <Chip label="Missing" color="warning" size="small" variant="outlined" sx={{ height: 18 }} />}
											</Stack>
										}
										secondary={scope.description}
									/>
								</ListItemButton>
							</ListItem>
						))}
					</List>
				)}
			</Paper>
		</Stack>
	);
}
